import { Field, Form, Formik } from 'formik';
import React, { useCallback, useState } from 'react';
import NewDesign from 'components/NewDesign';
import { useHistory, useLocation } from 'react-router';
import services from 'services';
import { isResponseSuccess } from 'helpers';
import { validationResetPassword } from 'consts/validateSchema';
import queryString from 'query-string';
import { V2_RESET_PASSWORD_URL } from 'consts/api';
import { AppRoutes } from 'consts/routesPath';
import InputField from 'customField/InputField';
import { useQueryParam } from 'hooks/useQueryParam';
import ThreeDotAnimated from 'components/ThreeDotAnimated';
import useResponsive from 'hooks/useResponsive';

const initialValues = {
  password: '',
  passwordConfirm: '',
};

const ResetPassword = () => {
  //! State
  const [state, setState] = useState({
    success: null,
    error: null,
  });
  const params = useQueryParam();
  const { isMobile } = useResponsive();
  const needResetPassword = params?.required === 'true';

  const location = useLocation();
  const parsedQueryString = queryString.parse(location.search);
  const token = parsedQueryString.token;

  const { error, success } = state;
  const history = useHistory();

  //! Function
  const handleOnSubmit = useCallback(
    async (values, { setSubmitting, resetForm }) => {
      if (values.password !== values.passwordConfirm) {
        setState((prev) => ({ ...prev, error: 'Confirm password not matched' }));
        return;
      }

      const requestBody = JSON.parse(JSON.stringify(values));
      delete requestBody.passwordConfirm;
      requestBody.token = token;
      setState((prev) => ({ ...prev, error: null }));
      try {
        const res = await services.post(V2_RESET_PASSWORD_URL, requestBody);
        if (isResponseSuccess(res.status) && res.data && res.data.status === 'success') {
          setState((prev) => ({ ...prev, success: true }));
          await new Promise((resolve) => setTimeout(resolve, 1500));
          window.location.href = AppRoutes.Login;
        } else {
          throw Error(res?.data?.error?.error || 'No response');
        }
      } catch (error) {
        const errorParsed = error?.response?.data;
        try {
          setState((prev) => ({ ...prev, error: errorParsed?.reason }));
        } catch (e) {
          setState((prev) => ({ ...prev, error: e.toString() }));
        }
      }

      setSubmitting(false);
    },
    [token, history]
  );

  //! Render
  return (
    <div className='component:ResetPassword p-3 d-flex justify-content-center align-items-center flex-column h-100 bg-color-white'>
      <Formik
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={handleOnSubmit}
        initialValues={initialValues}
        validationSchema={validationResetPassword}
      >
        {({ isSubmitting, values }) => {
          return (
            <Form
              className='d-flex flex-column gap-8px'
              style={{ minWidth: isMobile ? '98%' : 600 }}
            >
              <NewDesign.Typography component='h3' className='mb-2'>
                Reset Password
              </NewDesign.Typography>

              {needResetPassword && (
                <NewDesign.Alert color='primary' className='mb-2'>
                  Your password has <b>EXPIRED</b>! <br />
                  To improve protection and safety, it must be changed!
                </NewDesign.Alert>
              )}

              <div className='w-100'>
                <Field
                  component={InputField}
                  name='password'
                  label='Password'
                  placeholder='Input your password'
                  isShowHidePassword
                  required
                />
              </div>

              <div className='w-100'>
                <Field
                  component={InputField}
                  name='passwordConfirm'
                  label='Confirm Password'
                  placeholder='Confirm your password'
                  isShowHidePassword
                  required
                />
              </div>

              {error && (
                <NewDesign.Alert color='danger' className='mb-0'>
                  {error}
                </NewDesign.Alert>
              )}

              {success && (
                <NewDesign.Alert color='success' className='mb-0'>
                  <NewDesign.Typography>
                    Password has been reset successfully!
                    <br /> You'll be redirected to login page in a few seconds
                    <ThreeDotAnimated />
                  </NewDesign.Typography>
                </NewDesign.Alert>
              )}

              <div className='d-flex gap-5px flex-column w-100-div-1'>
                <NewDesign.Button
                  type='submit'
                  className='w-100-div-1'
                  isLoading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Confirm
                </NewDesign.Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ResetPassword;
